/**
 * Workspace Standard Detail Page
 *
 * Displays details of a workspace-scoped standard including
 * extracted clauses, metadata, and contextual action buttons.
 *
 * Route: /$workspaceSlug/standards/$standardId
 */

import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "@/packages/utils";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  Pencil,
  RefreshCw,
  Rocket,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react";
import { api } from "convex/_generated/api";
import { useMutation } from "convex/react";
import type { Id } from "@/packages/types";
import type { FunctionReturnType } from "convex/server";
import { Button } from "@/components/ui/primitives";
import { ConfirmDeleteDialog } from "@/components/ui/ConfirmDeleteDialog";
import { EditWorkspaceStandardDialog } from "@/components/workspace-standards/EditWorkspaceStandardDialog";
import { handleCreditExhaustedError } from "@/components/ai/credit-exhausted-modal-store";
import { useApiMutation } from "@/packages/features-shared";
import { useWorkspaceId } from "@/hooks/use-workspace-mutation";
import { springs } from "@/components/ui/motion";
import { ViewToggle } from "@/components/ui/PageHeader";
import { ChecklistAccordion } from "@/components/standards/ChecklistAccordion";
import type { ChecklistTemplateData } from "@/components/standards/types";
import { StatusBadge, formatDate } from "./shared";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

type WorkspaceStandardDetail = NonNullable<
  FunctionReturnType<typeof api.workspace_standards_queries.getById>
>;

/* ------------------------------------------------------------------ */
/* Shimmer skeleton primitive                                          */
/* ------------------------------------------------------------------ */

function Shimmer({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={`relative overflow-hidden rounded bg-border/40 ${className ?? ""}`}
      style={style}
    >
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.8s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/50 to-transparent" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Back Link                                                           */
/* ------------------------------------------------------------------ */

function BackLink() {
  const { t } = useTranslation("common");
  const { workspaceSlug } = useParams({ strict: false });
  return (
    <Link
      to="/$workspaceSlug/standards"
      params={{ workspaceSlug: workspaceSlug ?? "" }}
      className="group inline-flex items-center gap-2 text-[13px] font-medium text-secondary hover:text-primary transition-colors"
    >
      <span className="flex items-center justify-center w-6 h-6 rounded-md bg-card border border-border group-hover:border-accent/40 group-hover:bg-accent/5 transition-all">
        <ArrowLeft size={12} />
      </span>
      {t("workspaceStandards.detail.backToList")}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Hero Header                                                         */
/* ------------------------------------------------------------------ */

function HeroHeader({ standard }: { standard: WorkspaceStandardDetail }) {
  const { t } = useTranslation("common");
  const isExtracting = standard.status === "extracting";
  const isFailed = standard.status === "extraction_failed";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.smooth}
      className="relative rounded-2xl border border-border bg-card overflow-hidden"
    >
      {/* Accent top border */}
      <div className="h-1 bg-gradient-to-r from-accent via-accent/60 to-transparent" />

      <div className="p-6 space-y-4">
        {/* Title row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3.5 min-w-0">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-accent/10 shrink-0 mt-0.5">
              <FileText size={18} className="text-accent" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-primary tracking-[-0.01em] truncate">
                {standard.fileName}
              </h1>
              <div className="flex items-center gap-3 mt-1.5 text-[13px] text-secondary">
                <span>{standard.creatorName}</span>
                <span className="w-1 h-1 rounded-full bg-border" />
                <span>{formatDate(standard._creationTime)}</span>
                <span className="w-1 h-1 rounded-full bg-border" />
                <span className="text-muted">
                  {t("workspaceStandards.card.clauses", {
                    count: standard.clauses.length,
                  })}
                </span>
              </div>
            </div>
          </div>

          <StatusBadge status={standard.status} />
        </div>

        {/* Extraction progress bar */}
        {isExtracting && <ExtractionProgress />}

        {/* Failed state inline notice */}
        {isFailed && <FailedNotice />}
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Extraction progress visualization                                   */
/* ------------------------------------------------------------------ */

function ExtractionProgress() {
  const { t } = useTranslation("common");

  return (
    <div className="rounded-xl bg-amber-50/60 border border-amber-200/40 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            duration: 2,
            repeat: Number.POSITIVE_INFINITY,
            ease: "linear",
          }}
        >
          <RefreshCw size={14} className="text-amber-600" />
        </motion.div>
        <span className="text-[13px] font-medium text-amber-700">
          {t("workspaceStandards.status.extracting")}
        </span>
      </div>

      {/* Animated scan lines */}
      <div className="space-y-1.5">
        {[0.8, 0.6, 0.45, 0.7, 0.35].map((width) => (
          <motion.div
            key={width}
            className="h-1.5 rounded-full bg-amber-200/60"
            initial={{ scaleX: 0, originX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{
              delay: [0.8, 0.6, 0.45, 0.7, 0.35].indexOf(width) * 0.15,
              duration: 0.8,
              ease: "easeOut",
              repeat: Number.POSITIVE_INFINITY,
              repeatType: "reverse",
              repeatDelay: 1.5,
            }}
            style={{ width: `${width * 100}%` }}
          />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Failed extraction notice                                            */
/* ------------------------------------------------------------------ */

function FailedNotice() {
  const { t } = useTranslation("common");

  return (
    <div className="rounded-xl bg-red-50/60 border border-red-200/40 p-4">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-red-400" />
        <span className="text-[13px] font-medium text-red-700">
          {t("workspaceStandards.status.extraction_failed")}
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Generate checklist CTA                                              */
/* ------------------------------------------------------------------ */

function GenerateChecklistCTA({
  isGenerating,
  onGenerate,
}: {
  isGenerating: boolean;
  onGenerate: () => void;
}) {
  const { t } = useTranslation("common");

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.smooth, delay: 0.08 }}
      className="rounded-2xl border border-accent/20 bg-accent/5 p-5 space-y-3"
    >
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-accent/10 shrink-0">
          <Sparkles size={16} className="text-accent" />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <p className="text-[14px] font-medium text-primary">
            {t("workspaceStandards.detail.generateChecklist")}
          </p>
          <p className="text-[13px] text-secondary leading-relaxed">
            {t("workspaceStandards.detail.generateChecklistDescription")}
          </p>
        </div>
      </div>

      <Button
        variant="primary"
        size="sm"
        icon={isGenerating ? Loader2 : Sparkles}
        onClick={onGenerate}
        loading={isGenerating}
        disabled={isGenerating}
      >
        {isGenerating
          ? t("workspaceStandards.detail.generatingChecklist")
          : t("workspaceStandards.detail.generateChecklist")}
      </Button>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Checklist generation progress                                       */
/* ------------------------------------------------------------------ */

function ChecklistGenerationProgress() {
  const { t } = useTranslation("common");

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.smooth}
      className="rounded-2xl border border-accent/20 bg-accent/5 p-5"
    >
      <div className="flex items-center gap-3">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            duration: 2,
            repeat: Number.POSITIVE_INFINITY,
            ease: "linear",
          }}
        >
          <Loader2 size={16} className="text-accent" />
        </motion.div>
        <span className="text-[13px] font-medium text-primary">
          {t("workspaceStandards.detail.generatingChecklist")}
        </span>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Checklist generation failed notice                                   */
/* ------------------------------------------------------------------ */

function ChecklistGenerationFailedNotice({
  onRetry,
  isRetrying,
}: {
  onRetry: () => void;
  isRetrying: boolean;
}) {
  const { t } = useTranslation("common");

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.smooth}
      className="rounded-2xl border border-red-200/40 bg-red-50/60 p-5 space-y-3"
    >
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-red-400" />
        <span className="text-[13px] font-medium text-red-700">
          {t("workspaceStandards.detail.checklistGenerationFailedNotice")}
        </span>
      </div>
      <Button
        variant="accent-soft"
        size="sm"
        icon={RefreshCw}
        onClick={onRetry}
        loading={isRetrying}
        disabled={isRetrying}
      >
        {t("workspaceStandards.detail.retryGeneration")}
      </Button>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Actions bar                                                         */
/* ------------------------------------------------------------------ */

function ActionsBar({
  standard,
  onEdit,
  onDelete,
  onReupload,
  onPublish,
  isPublishing,
}: {
  standard: WorkspaceStandardDetail;
  onEdit: () => void;
  onDelete: () => void;
  onReupload: () => void;
  onPublish: () => void;
  isPublishing: boolean;
}) {
  const { t } = useTranslation("common");
  const isFailed = standard.status === "extraction_failed";
  const canPublish = standard.status === "approved";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.smooth, delay: 0.05 }}
      className="flex items-center gap-2 flex-wrap"
    >
      <Button variant="secondary" size="sm" icon={Pencil} onClick={onEdit}>
        {t("workspaceStandards.card.actions.edit")}
      </Button>

      {canPublish && (
        <Button
          variant="primary"
          size="sm"
          icon={Rocket}
          onClick={onPublish}
          loading={isPublishing}
          disabled={isPublishing}
        >
          {t("workspaceStandards.detail.publish")}
        </Button>
      )}

      {isFailed && (
        <Button
          variant="accent-soft"
          size="sm"
          icon={RefreshCw}
          onClick={onReupload}
        >
          {t("workspaceStandards.card.actions.reupload")}
        </Button>
      )}

      {standard.documentUrl && (
        <a
          href={standard.documentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-button border border-border px-2 py-1.5 text-xs font-medium text-secondary hover:text-primary hover:border-accent/40 hover:bg-accent/5 transition-all"
        >
          <Download size={12} />
          {t("workspaceStandards.detail.downloadDocument")}
        </a>
      )}

      <div className="flex-1" />

      <Button
        variant="ghost"
        size="sm"
        icon={Trash2}
        onClick={onDelete}
        className="text-red-500 hover:text-red-600 hover:bg-red-50"
      >
        {t("workspaceStandards.card.actions.delete")}
      </Button>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Clause card                                                         */
/* ------------------------------------------------------------------ */

function ClauseCard({
  clause,
  index,
}: {
  clause: WorkspaceStandardDetail["clauses"][number];
  index: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasDescription = Boolean(clause.description);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.smooth, delay: index * 0.03 }}
      className="group rounded-xl border border-border bg-card hover:border-accent/30 transition-colors"
    >
      <button
        type="button"
        onClick={() => hasDescription && setExpanded(!expanded)}
        className={`w-full text-left p-4 flex items-start gap-3 ${hasDescription ? "cursor-pointer" : "cursor-default"}`}
      >
        {/* Clause number badge */}
        {clause.clauseNumber && (
          <span className="shrink-0 flex items-center justify-center min-w-[2.5rem] h-7 rounded-lg bg-accent/8 text-[11px] font-bold text-accent tracking-wide tabular-nums">
            {clause.clauseNumber}
          </span>
        )}

        <div className="flex-1 min-w-0">
          {clause.title && (
            <span className="text-[14px] font-medium text-primary leading-snug">
              {clause.title}
            </span>
          )}
        </div>

        {hasDescription && (
          <span className="shrink-0 text-muted group-hover:text-secondary transition-colors mt-0.5">
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        )}
      </button>

      <AnimatePresence>
        {expanded && clause.description && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0 pl-[calc(1rem+2.5rem+0.75rem)]">
              <p className="text-[13px] text-secondary leading-relaxed">
                {clause.description}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Clauses section                                                     */
/* ------------------------------------------------------------------ */

function ClausesSection({
  clauses,
  status,
}: {
  clauses: WorkspaceStandardDetail["clauses"];
  status: string;
}) {
  const { t } = useTranslation("common");
  const [search, setSearch] = useState("");
  const isExtracting = status === "extracting";

  const filtered = search
    ? clauses.filter(
        (c) =>
          c.title?.toLowerCase().includes(search.toLowerCase()) ||
          c.clauseNumber?.toLowerCase().includes(search.toLowerCase()) ||
          c.description?.toLowerCase().includes(search.toLowerCase()),
      )
    : clauses;

  // Empty — extracting
  if (clauses.length === 0 && isExtracting) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...springs.smooth, delay: 0.1 }}
        className="rounded-2xl border border-dashed border-amber-200/60 bg-amber-50/20 p-10 flex flex-col items-center gap-4"
      >
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{
            duration: 3,
            repeat: Number.POSITIVE_INFINITY,
            ease: "easeInOut",
          }}
          className="w-14 h-14 rounded-2xl bg-amber-100/60 flex items-center justify-center"
        >
          <FileText size={24} className="text-amber-500" />
        </motion.div>
        <div className="text-center space-y-1">
          <p className="text-[14px] font-medium text-primary">
            {t("workspaceStandards.status.extracting")}
          </p>
          <p className="text-[13px] text-secondary max-w-[260px]">
            {t("workspaceStandards.detail.noClauses")}
          </p>
        </div>
      </motion.div>
    );
  }

  // Empty — no clauses
  if (clauses.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...springs.smooth, delay: 0.1 }}
        className="rounded-2xl border border-dashed border-border bg-card/50 p-10 flex flex-col items-center gap-4"
      >
        <div className="w-14 h-14 rounded-2xl bg-border/30 flex items-center justify-center">
          <FileText size={24} className="text-muted" />
        </div>
        <div className="text-center space-y-1">
          <p className="text-[14px] font-medium text-primary">
            {t("workspaceStandards.detail.clausesTitle")}
          </p>
          <p className="text-[13px] text-secondary max-w-[260px]">
            {t("workspaceStandards.detail.noClauses")}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.smooth, delay: 0.1 }}
      className="space-y-3"
    >
      {/* Section header + search */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[15px] font-semibold text-primary">
          {t("workspaceStandards.detail.clausesTitle")}
          <span className="ml-2 text-[13px] font-normal text-muted">
            {clauses.length}
          </span>
        </h2>

        {clauses.length > 5 && (
          <div className="relative">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("workspaceStandards.search")}
              className="h-7 w-48 rounded-lg border border-border bg-card pl-7 pr-2.5 text-xs text-primary placeholder:text-muted focus:outline-none focus:border-accent/40 focus:ring-1 focus:ring-accent/20 transition-all"
            />
          </div>
        )}
      </div>

      {/* Clause list */}
      <div className="space-y-2">
        {filtered.map((clause, i) => (
          <ClauseCard key={clause._id} clause={clause} index={i} />
        ))}

        {search && filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-[13px] text-muted">
            {t("workspaceStandards.noResults")}
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Skeleton loading state                                              */
/* ------------------------------------------------------------------ */

function DetailSkeleton() {
  return (
    <div className="max-w-3xl space-y-6">
      <BackLink />

      {/* Hero skeleton */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="h-1 bg-border/30" />
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3.5">
            <Shimmer className="w-10 h-10 !rounded-xl" />
            <div className="space-y-2 flex-1">
              <Shimmer className="h-6 w-52" />
              <div className="flex gap-3">
                <Shimmer className="h-4 w-24" />
                <Shimmer className="h-4 w-20" />
                <Shimmer className="h-4 w-16" />
              </div>
            </div>
            <Shimmer className="h-5 w-16 !rounded-full" />
          </div>
        </div>
      </div>

      {/* Action buttons skeleton */}
      <div className="flex gap-2">
        <Shimmer className="h-7 w-16 !rounded-button" />
        <Shimmer className="h-7 w-32 !rounded-button" />
      </div>

      {/* Clauses skeleton */}
      <div className="space-y-3">
        <Shimmer className="h-5 w-36" />
        {[0.85, 0.7, 0.9, 0.6].map((w) => (
          <div
            key={w}
            className="rounded-xl border border-border bg-card p-4 flex items-start gap-3"
          >
            <Shimmer className="w-10 h-7 !rounded-lg" />
            <Shimmer
              className="h-5 flex-1"
              style={{ maxWidth: `${w * 100}%` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Not-found state                                                     */
/* ------------------------------------------------------------------ */

function NotFoundState() {
  const { t } = useTranslation("common");

  return (
    <div className="max-w-3xl space-y-6">
      <BackLink />
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springs.smooth}
        className="rounded-2xl border border-dashed border-border bg-card/50 p-16 flex flex-col items-center gap-5"
      >
        <div className="w-16 h-16 rounded-2xl bg-border/20 flex items-center justify-center">
          <FileText size={28} className="text-muted" />
        </div>
        <div className="text-center space-y-1.5">
          <h2 className="text-[16px] font-semibold text-primary">
            {t("workspaceStandards.detail.notFoundTitle")}
          </h2>
          <p className="text-[13px] text-secondary max-w-[280px]">
            {t("workspaceStandards.detail.notFoundDescription")}
          </p>
        </div>
      </motion.div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Page Component                                                 */
/* ------------------------------------------------------------------ */

export function WorkspaceStandardDetailPage() {
  const { t } = useTranslation("common");
  const { standardId, workspaceSlug } = useParams({ strict: false });
  const navigate = useNavigate();
  const workspaceId = useWorkspaceId();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isGeneratingChecklist, setIsGeneratingChecklist] = useState(false);
  const [activeTab, setActiveTab] = useState<"clauses" | "checklist">(
    "clauses",
  );

  const generateChecklist = useMutation(
    api.workspace_standards_mutations.generateChecklist,
  );
  const publishMutation = useApiMutation(
    api.workspace_standards_mutations.publish,
    {
      name: "publish-workspace-standard",
      successMessage: t("workspaceStandards.detail.publishSuccess"),
    },
  );

  const { data: standard, isLoading } = useQuery(
    convexQuery(api.workspace_standards_queries.getById, {
      id: standardId as Id<"officialStandards">,
      workspaceId,
    }),
  );

  const { data: checklistTemplateResult } = useQuery(
    convexQuery(api.admin_standards_queries.getChecklistTemplate, {
      standardId: standardId as Id<"officialStandards">,
    }),
  );

  const checklistData: ChecklistTemplateData | null =
    checklistTemplateResult?.checklistData ?? null;
  const hasChecklist = !!checklistData;

  useEffect(() => {
    if (hasChecklist) setActiveTab("checklist");
  }, [hasChecklist]);

  const deleteMutation = useApiMutation(
    api.workspace_standards_mutations.deleteStandard,
    {
      name: "delete-workspace-standard",
      successMessage: t("workspaceStandards.detail.deleteSuccess"),
    },
  );

  const handleGenerateChecklist = async () => {
    if (!standard) return;
    setIsGeneratingChecklist(true);
    try {
      await generateChecklist({ standardId: standard._id });
      toast.success(t("workspaceStandards.detail.generateChecklistSuccess"));
    } catch (error) {
      if (!handleCreditExhaustedError(error)) {
        toast.error(t("workspaceStandards.detail.generateChecklistFailed"));
      }
    } finally {
      setIsGeneratingChecklist(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!standard) return;
    await deleteMutation.mutateAsync({ standardId: standard._id });
    setDeleteDialogOpen(false);
    navigate({
      to: "/$workspaceSlug/standards",
      params: { workspaceSlug: workspaceSlug ?? "" },
    });
  };

  if (isLoading) return <DetailSkeleton />;
  if (!standard) return <NotFoundState />;

  return (
    <div className="max-w-3xl space-y-5">
      <BackLink />
      <HeroHeader standard={standard} />

      <ActionsBar
        standard={standard}
        onEdit={() => setEditDialogOpen(true)}
        onDelete={() => setDeleteDialogOpen(true)}
        onReupload={() => {
          toast.info(t("workspaceStandards.detail.reuploadComingSoon"));
        }}
        onPublish={() =>
          publishMutation.mutateAsync({ standardId: standard._id })
        }
        isPublishing={publishMutation.isPending}
      />

      {/* Generate checklist CTA — shown when clauses are extracted but no checklist yet */}
      {standard.status === "extracted" && standard.clauses.length > 0 && (
        <GenerateChecklistCTA
          isGenerating={isGeneratingChecklist}
          onGenerate={handleGenerateChecklist}
        />
      )}

      {/* Checklist generation in progress */}
      {standard.status === "generating_checklist" && (
        <ChecklistGenerationProgress />
      )}

      {/* Checklist generation failed */}
      {standard.status === "checklist_generation_failed" && (
        <ChecklistGenerationFailedNotice
          onRetry={handleGenerateChecklist}
          isRetrying={isGeneratingChecklist}
        />
      )}

      {/* Tab bar when checklist is available */}
      {hasChecklist && (
        <ViewToggle
          options={[
            {
              id: "clauses",
              label: t("workspaceStandards.detail.clausesTitle"),
              active: activeTab === "clauses",
            },
            {
              id: "checklist",
              label: `${t("workspaceStandards.detail.checklistTab")} (${checklistData.categories?.length ?? 0})`,
              active: activeTab === "checklist",
            },
          ]}
          onViewChange={(id) => setActiveTab(id as "clauses" | "checklist")}
        />
      )}

      {/* Tab content */}
      {activeTab === "clauses" || !hasChecklist ? (
        <ClausesSection clauses={standard.clauses} status={standard.status} />
      ) : null}

      {activeTab === "checklist" && hasChecklist && checklistData && (
        <ChecklistAccordion data={checklistData} />
      )}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteMutation.isPending}
        title={t("workspaceStandards.detail.deleteTitle")}
        description={t("workspaceStandards.detail.deleteDescription")}
        confirmMatchText={standard.fileName}
        confirmLabel={t("workspaceStandards.card.actions.delete")}
      />

      <EditWorkspaceStandardDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        standard={standard}
      />
    </div>
  );
}
